/**
 * PaymentMethodSelector — FUNC-131. Selector de método de pago del wizard
 * público (paciente nuevo y existente): exactamente DOS opciones.
 *
 *  1) Transferencia / Efectivo → declara 'transferencia' (precio preferente)
 *  2) Débito / Crédito         → declara 'tarjeta' (precio de lista)
 *
 * Al elegir tarjeta en una sesión donde el método cambia el precio (presencial),
 * se despliega un aviso que explica el valor preferente. El precio mostrado lo
 * calcula el padre con `getSessionPrice` (lib/utils/pricing.ts) a partir del
 * mismo `metodo`, así que se actualiza al instante.
 */

import React from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { getAhorroTransferencia, type MetodoPagoDeclarado } from '../../lib/utils/pricing';
import './PaymentMethodSelector.css';

interface PaymentMethodSelectorProps {
  metodo: MetodoPagoDeclarado;
  onChange: (metodo: MetodoPagoDeclarado) => void;
  tipo: string;
  duracion: number;
  modalidad: string;
  fechaHora?: string | null;
  /** Paciente con tarifa diferenciada: el método no cambia su valor → sin aviso de ahorro. */
  tarifaFija?: boolean;
}

const clp = (n: number) => `$${n.toLocaleString('es-CL')}`;

const PaymentMethodSelector: React.FC<PaymentMethodSelectorProps> = ({
  metodo, onChange, tipo, duracion, modalidad, fechaHora, tarifaFija = false,
}) => {
  const reduceMotion = useReducedMotion();
  const ahorro = tarifaFija ? 0 : getAhorroTransferencia({ tipo, duracion, modalidad, fechaHora });
  const esTarjeta = metodo === 'tarjeta';
  const mostrarAviso = esTarjeta && ahorro > 0;

  return (
    <div className="pms">
      <div className="pms-group" role="group" aria-label="Seleccionar método de pago">
        <button
          type="button"
          className={`pms-pill${!esTarjeta ? ' selected' : ''}`}
          onClick={() => onChange('transferencia')}
          aria-pressed={!esTarjeta}
        >
          <i className="fas fa-university" aria-hidden="true" />
          Transferencia / Efectivo
        </button>
        <button
          type="button"
          className={`pms-pill${esTarjeta ? ' selected' : ''}`}
          onClick={() => onChange('tarjeta')}
          aria-pressed={esTarjeta}
        >
          <i className="fas fa-credit-card" aria-hidden="true" />
          Débito / Crédito
        </button>
      </div>

      {/* Región viva siempre montada: el lector de pantalla anuncia el cambio de valor. */}
      <div aria-live="polite">
        <AnimatePresence initial={false}>
          {mostrarAviso && (
            <motion.div
              key="pms-callout"
              className="pms-callout"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: reduceMotion ? 0 : 0.25, ease: 'easeOut' }}
            >
              <div className="pms-callout__inner">
                <i className="fas fa-heart" aria-hidden="true" />
                <p>
                  Al pagar con transferencia o efectivo accedes a un valor preferente: es nuestra
                  forma de cuidar también tu bienestar financiero.{' '}
                  <strong>Ahorras {clp(ahorro)}</strong> frente a débito o crédito.
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        {!mostrarAviso && (
          <p className="pms-hint">
            {ahorro > 0
              ? `Valor preferente: ahorras ${clp(ahorro)} frente a débito o crédito.`
              : 'El valor de tu sesión es el mismo con cualquier método de pago.'}
          </p>
        )}
      </div>
    </div>
  );
};

export default PaymentMethodSelector;
